/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type MemoExoticComponent } from 'react';
import { css, jsx } from '@compiled/react';
import { fg } from '@atlaskit/platform-feature-flags';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { token } from '@atlaskit/tokens';
import { useIntl } from 'react-intl';
import type { EmojiDescription, OnEmojiEvent } from '../../types';
import CachingEmoji from '../common/CachingEmoji';
import type { VirtualItem as VirtualItemContext } from '@tanstack/react-virtual';
import { useEmojiPickerListContext } from '../../hooks/useEmojiPickerListContext';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { CategoryGroupKey } from './categories';
import { messages } from '../i18n';

const emojiItem = css({
	display: 'inline-block',
	textAlign: 'center',
	width: '40px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .emoji-common-node': {
		cursor: 'pointer',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.medium'),
		width: '24px',
		height: '24px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .emoji-common-placeholder': {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginTop: token('space.100'),
		marginRight: token('space.100'),
		marginBottom: token('space.100'),
		marginLeft: token('space.100'),
		minWidth: '24px',
		maxWidth: '24px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .emoji-common-node .emoji-common-placeholder': {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
	},
	// Fit non-square emoji to square
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .emoji-common-node > img': {
		position: 'relative',
		left: '50%',
		top: '50%',
		transform: 'translateX(-50%) translateY(-50%)',
		maxHeight: '24px',
		maxWidth: '24px',
		display: 'block',
	},
	// Scale sprite to fit regardless of default emoji size
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .emoji-common-node > .emoji-common-emoji-sprite': {
		height: '24px',
		width: '24px',
	},
});

const emojiPickerRow = css({
	marginLeft: token('space.100'),
});

const emojiPickerRowList = css({
	listStyle: 'none',
	padding: 0,
	margin: 0,
	marginLeft: token('space.100'),
});

const teamojiRefreshExperimentName = 'platform_teamoji_26_refresh_emoji_picker';

export interface Props {
	category: CategoryGroupKey;
	emojis: EmojiDescription[];
	onDelete?: OnEmojiEvent;
	onFocus?: OnEmojiEvent;
	onMouseLeave?: () => void;
	onMouseMove?: OnEmojiEvent;
	onSelected?: OnEmojiEvent;
	showDelete: boolean;
	title: string;
	virtualItemContext?: VirtualItemContext;
}

const EmojiPickerEmojiRow = ({
	emojis,
	onSelected,
	onMouseMove,
	onMouseLeave,
	onFocus,
	title,
	showDelete,
	onDelete,
	virtualItemContext,
}: Props): JSX.Element => {
	const { currentEmojisFocus, setEmojisFocus } = useEmojiPickerListContext();
	const rowIndex = virtualItemContext?.index || 0;
	const { formatMessage } = useIntl();
	const fitToHeight = expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true)
		? 24
		: undefined;
	const preventFocusOnMouseDown = FeatureGates.getExperimentValue(
		teamojiRefreshExperimentName,
		'isEnabled',
		false,
	);
	const handleFocus: (index: number) => OnEmojiEvent<HTMLSpanElement> =
		(index) => (emojiId, emoji, event) => {
			setEmojisFocus({
				rowIndex,
				columnIndex: index,
			});
			onFocus && onFocus(emojiId, emoji, event);
		};
	if (fg('platform_a11y_fixes_reaction_emoji')) {
		return (
			<ul css={emojiPickerRowList} role="list">
				{emojis.map((emoji, index) => {
					const { shortName, id } = emoji;
					const key = id ? `${id}-${title}` : `${shortName}-${title}`;
					const focus =
						currentEmojisFocus.rowIndex === rowIndex && currentEmojisFocus.columnIndex === index;
					return (
						<li key={key} css={emojiItem}>
							<CachingEmoji
								emoji={emoji}
								selectOnHover={true}
								onSelected={onSelected}
								onMouseMove={onMouseMove}
								onFocus={handleFocus(index)}
								showDelete={showDelete}
								onDelete={onDelete}
								placeholderSize={24}
								data-focus-index={`${rowIndex}-${index}`}
								tabIndex={focus ? 0 : -1}
								aria-roledescription={formatMessage(messages.emojiButtonRoleDescription)}
								shouldBeInteractive
								fitToHeight={fitToHeight}
								preventFocusOnMouseDown={preventFocusOnMouseDown}
							/>
						</li>
					);
				})}
			</ul>
		);
	}

	return (
		<div css={emojiPickerRow} role="presentation">
			{emojis.map((emoji, index) => {
				const { shortName, id } = emoji;
				const key = id ? `${id}-${title}` : `${shortName}-${title}`;
				const focus =
					currentEmojisFocus.rowIndex === rowIndex && currentEmojisFocus.columnIndex === index;
				return (
					<span
						css={emojiItem}
						key={key}
						role={fg('platform_a11y_fixes_reaction_emoji') ? 'presentation' : 'gridcell'}
						{...(fg('platform_a11y_fixes_reaction_emoji') ? {} : { 'aria-colindex': index + 1 })}
						onMouseLeave={onMouseLeave}
						onBlur={onMouseLeave}
					>
						<CachingEmoji
							emoji={emoji}
							selectOnHover={true}
							onSelected={onSelected}
							onMouseMove={onMouseMove}
							onFocus={handleFocus(index)}
							showDelete={showDelete}
							onDelete={onDelete}
							placeholderSize={24}
							data-focus-index={`${rowIndex}-${index}`}
							tabIndex={focus ? 0 : -1}
							aria-roledescription={formatMessage(messages.emojiButtonRoleDescription)}
							shouldBeInteractive
							fitToHeight={fitToHeight}
							preventFocusOnMouseDown={preventFocusOnMouseDown}
						/>
					</span>
				);
			})}
		</div>
	);
};

const _default_1: MemoExoticComponent<
	({
		emojis,
		onSelected,
		onMouseMove,
		onMouseLeave,
		onFocus,
		title,
		showDelete,
		onDelete,
		virtualItemContext,
	}: Props) => JSX.Element
> = memo(EmojiPickerEmojiRow);
export default _default_1;
